import { useContext, useEffect, useState } from 'react'
import { People } from '@mui/icons-material'
import ListUsers from './ListUsers'
import { useNavigate,useSearchParams } from 'react-router-dom'
import { useIsMounted } from '../utils/ReactUtils.js'

import ListViewCommandBar from '../commandBar/ListViewCommandBar'
import Page from '../layout/Page'
import { Button, Paper, Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'

import UserRolesSelection from './UserRolesSelection.js'
import RoleContext from '../auth/RoleContext.js'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { getToken } from '../auth/auth.js'
import { ApiGateway } from '../config/config.js'
import axios from 'axios'
import { getGroupMembership } from './data/membership.js'
import { Link } from 'react-router-dom'
import { FileDownload } from '@mui/icons-material'
import { download, generateCsv, mkConfig } from 'export-to-csv'

export default function UserReportPage (props = {}) {
  const roleDetails = useContext(RoleContext)
  const [userRows, setUserRows] = useState([])
  const [searching, setSearching] = useState(false)

  const breadcrumb = <span><People/> QSE Portal User Report</span>

  const [showFilters, setShowFilters] = useState(true)
  const toggleFilters = () => {
    setShowFilters(!showFilters)
  }

  const getUserList = async() => {
    setSearching(true)
    const token = await getToken()      
    
    const basePath = `${ApiGateway.user}/list`
    const options = {
      headers: {        
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.idToken}`
      }
    }
    try {
      const response = await axios.get(basePath, options )
      const users = response.data.filter(u => u.isActive !== false)
      
      const userChunks = ((arr, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
          const chunk = arr.slice(i, i + chunkSize);
          chunks.push(chunk);
        }
        return chunks;
      })(users, 20)

      for (const chunk of userChunks) {
        const gr = await Promise.all(chunk.map(async u => {
          u.groups = await getGroupMembership({ email: u.email })
          return u
        }))
      }
      
      setUserRows(users)
    } catch (error) {
      console.error(error)
    } finally {
      setSearching(false)
    }
    
    return
  }
  
  useEffect(() => {
    if (!userRows || userRows.length === 0) {
      getUserList()
    }
  }, [userRows])

  const userColumns = [
      {
        accessorKey: 'koId',
        header: 'KO ID',
        size: 125,
        grow: false,
        editable: false,
        filterVariant: 'text'
      },
      {
        accessorKey: 'name',
        accessorFn: (row) => {
          return [row.firstName, row.lastName].join(' ')
        },
        Cell: ({row}) => {
          return [row.original.firstName, row.original.lastName].filter(v => v.trim().length > 0).join(' ')
        },
        header: 'Name',
        size: 250,
        grow: false,
        filterVariant: 'text'
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 200,
        grow: true,
        editable: false
      },
      {
        accessorKey: 'roles',
        accessorFn: (row) => {
          return row.roles.map(r => r.roleName).join(', ')
        },
        header: 'Role(s)',
        size: 150,
        grow: true,
        editable: false
      },
      {
        accessorKey: 'groups',
        accessorFn: (row) => {
          return row.groups.map(g => g.displayName).join(', ')
        },
        header: 'Group(s)',
        size: 150,
        grow: true,
        editable: false
      }
    ]

    const table = useMaterialReactTable({
        layoutMode: 'grid',
        columnFilterModeOptions: [
          'fuzzy',
          'greaterThan',
          'lessThan'
        ],
        columns: userColumns,
        data: userRows,
        enableColumnActions: false,
        enableColumnFilters: true,
        enableFilterMatchHighlighting: false,
        initialState: {
          showColumnFilters: true,
          pagination: {
            pageIndex: 0,
            pageSize: 5000
          }
        },
        enablePagination: false,
        paginationDisplayMode: 'default',
        enableSorting: true,
        enableEditing: false,
        muiTableContainerProps: {
          sx: { minHeight: props.minHeight ?? 300 },
    
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize: '.8rem !important',
            padding: '4px !important'
          }
        },
        enableTopToolbar: false,
        enableBottomToolbar: true,
        renderBottomToolbarCustomActions: ({table}) => {
          return <Box sx={{display: 'flex'}}>
            <Button onClick={handleExportData} startIcon={<FileDownload />}>
              Export to CSV
            </Button>
          </Box>
        }
      });
      //() => handleExportRows(table.getPrePaginationRowModel().rows)

  const handleExportData = () => {
    const rowData = table.getPrePaginationRowModel().rows.map((row) => {
      const r = row.original
  
      const n = {
        koId: r.koId,
        name: [r.firstName, r.lastName].filter(v => v.trim().length > 0).join(' '),
        email: r.email,
        roles: r.roles.map(role => role.roleName).join('; '),
        groups: r.groups.map(g => g.displayName).join('; ')
      }
      return n
    })
    const csvConfig = mkConfig({
      fieldSeparator: ',',
      decimalSeparator: '.',
      useKeysAsHeaders: true,
      filename: `qse_portal_users`,
      fileExtension: 'csv',
      quoteStrings: true
    })
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  }

  return (
    <Page { ...props} breadcrumb={breadcrumb} commandBar={<ListViewCommandBar actionButtons={<Link to='/users/list'>User Management</Link>} showFilters={false} showAdd={false} showCancel={false} /> }>
      {searching && <Typography variant='h5'>Loading, please wait...</Typography>}
      {!searching &&
          <MaterialReactTable table={table}
        />
      }
    </Page>
  )
}